import { NavLink, useLocation } from 'react-router-dom'

import {
  Badge,
  ChartColumnIcon,
  CalendarIcon,
  CheckIcon,
  DropdownMenu,
  ExternalLinkIcon,
  MessagesSquareIcon,
  PaintBucketIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useIsOneBufferOrg } from '~publish/contexts/AccountContext'
import { buildStartPageEditorUrl } from '~publish/helpers/externalUrls'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { formatCompactNumber } from '~publish/helpers/numberFormatters'

import type { MainSidebarChannel } from './useChannels'
import { useChannelUrl } from '../../hooks/useChannelUrl'
import { useCommentsChannelUrl } from '../../hooks/useCommentsChannelUrl'

import styles from './ChannelHoverMenu.module.css'

import type { JSX } from 'react'

type ChannelHoverMenuProps = {
  channel: MainSidebarChannel
  handleNavigation: (channelId: string) => void
  queueCount: number
  queueCountLimit?: number
  communityCount: number
  /** Whether `communityCount` includes mentions (drives the a11y label). */
  includesMentions: boolean
}

export function ChannelHoverMenu({
  channel,
  handleNavigation,
  queueCount,
  queueCountLimit,
  communityCount,
  includesMentions,
}: ChannelHoverMenuProps): JSX.Element {
  const scheduleUrl = useChannelUrl(channel.id)
  const commentsUrl = useCommentsChannelUrl(channel.id)
  const isOneBufferOrg = useIsOneBufferOrg()

  const isStartPage = channel.service === 'startPage'

  const location = useLocation()
  const isActive = (url: string): boolean =>
    location.pathname === url || location.pathname.startsWith(`${url}/`)

  const handleSubNavClick = (): void => {
    handleNavigation(channel.id)
  }

  const insightsUrl = `/insights/${channel.id}`

  return (
    <>
      <DropdownMenu.Label>
        <span translate="no">{getChannelNameForDisplay(channel)}</span>
      </DropdownMenu.Label>

      {/* Publish - always shown */}
      <DropdownMenu.Item asChild>
        <NavLink to={scheduleUrl} onClick={handleSubNavClick}>
          <CalendarIcon />
          Publish
          {isActive(scheduleUrl) ? (
            <CheckIcon className={styles.activeIcon} />
          ) : (
            queueCount > 0 && (
              <span className={styles.count}>
                {formatCompactNumber(queueCount, queueCountLimit)}
                <VisuallyHidden> scheduled posts</VisuallyHidden>
              </span>
            )
          )}
        </NavLink>
      </DropdownMenu.Item>

      {/* Community - only shown for One Buffer organisations */}
      {isOneBufferOrg && (
        <DropdownMenu.Item asChild>
          <NavLink to={commentsUrl} onClick={handleSubNavClick}>
            <MessagesSquareIcon />
            Community
            {isActive(commentsUrl) ? (
              <CheckIcon className={styles.activeIcon} />
            ) : (
              communityCount > 0 && (
                <Badge variant="brand" size="small">
                  {communityCount}
                  <VisuallyHidden>
                    {includesMentions
                      ? ' unanswered comments and mentions'
                      : ' unreplied comments'}
                  </VisuallyHidden>
                </Badge>
              )
            )}
          </NavLink>
        </DropdownMenu.Item>
      )}

      <DropdownMenu.Item asChild>
        <NavLink to={insightsUrl} onClick={handleSubNavClick}>
          <ChartColumnIcon />
          Insights
          {isActive(insightsUrl) && <CheckIcon className={styles.activeIcon} />}
        </NavLink>
      </DropdownMenu.Item>

      {/* Editor - only shown for Start Page channels */}
      {isStartPage && (
        <DropdownMenu.Item asChild>
          <a
            href={buildStartPageEditorUrl(channel.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PaintBucketIcon />
            Editor
            <ExternalLinkIcon size="xsmall" className={styles.externalIcon} />
          </a>
        </DropdownMenu.Item>
      )}
    </>
  )
}
